import _ from 'lodash';
import moment from 'moment';

import { RecipeCategoryEnum } from 'Common/Constants/Enumeration/RecipeCategoryEnum';
import {
  ReimbursableMealFieldEnum as Fields,
  RecipeFieldEnum as RecipeFields,
  CycleMenuRecipeFieldEnum as CycleMenuRecipeFields,
} from 'Common/FieldEnum';
import { COMPONENT_TYPE } from 'Common/Components/NutritionalHover/constants/constants';
import { EDIT_MENU_TYPES } from 'Pages/managemenu/helpers/Constants';
import * as ProdRecordHelpers from 'Pages/manageproduction/helpers/productionRecord';

import {
  ANALYSIS_VALUE_PRECISION,
  ALACARTE_RECIPE_CATEGORY,
  EMPTY_RECIPE_WITH_EMPTY_CONTRIBUTIONS,
  DAYS_OF_THE_WEEK,
  VEGETABLE_CONTRIBUTION_FIELDS,
} from '../constants';

/**
 * Reorders the items in a list
 * @param {Array} list the list of items
 * @param {Number} startIndex the start index
 * @param {Number} endIndex the end index
 * @returns the list with the item inserted at the endIndex
 */
export const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Moves an item from one list to another list.
 * @param {Array} source - the source list
 * @param {Array} destination - the destination list
 * @param {Number} sourceIndex - the index of the item being moved
 * @param {Number} destIndex - the index of the item in the destination list
 * @returns {Object} droppableIds mapped to their updated lists
 */
export const move = (source = [], destination = [], sourceIndex, destIndex) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(sourceIndex, 1);

  destClone.splice(destIndex, 0, removed);

  return {
    source: sourceClone,
    dest: destClone,
  };
};

/**
 * Adds an item to the list based on the index.
 *
 * @param {Array} destination - the list to add to
 * @param {Object} item - the item being added
 * @param {Number} index - the index indicating where to insert the item
 * @returns {Array} list with item added
 */
export const add = (destination = [], item, index) => {
  const destClone = Array.from(destination);

  if (_.isNil(index)) {
    destClone.push(item);
  } else {
    destClone.splice(index, 0, item);
  }
  return destClone;
};

export const hasValue = value => {
  return !(_.isNil(value) || isNaN(value) || (typeof value === 'string' && value.length === 0));
};

export const calculateTotalVeg = (contributions) => {
  const total =
    contributions.DarkGreenVeg +
    contributions.RedOrangeVeg +
    contributions.LegumesVeg +
    contributions.StarchyVeg +
    contributions.OtherVeg +
    contributions.AdditionalVeg;

  return parseFloat(!hasValue(total) ? 0 : total.toFixed(3));
};

export const calculateTotalGrain = (contributions) => {
  return contributions.WholeGrain + contributions.EnrichedGrain || 0;
};

export const initializeContributionsPerMeasure = () => {
  return {
    Fruit: 0,
    DarkGreenVeg: 0,
    RedOrangeVeg: 0,
    LegumesVeg: 0,
    StarchyVeg: 0,
    OtherVeg: 0,
    AdditionalVeg: 0,
    WholeGrain: 0,
    EnrichedGrain: 0,
    MeatAlt: 0,
    Milk: 0,
    TotalVeg: 0,
    TotalGrain: 0,
  };
};

export const calculateContributionsPerMeasure = (recipe) => {
  const contributions = initializeContributionsPerMeasure();
  const {
    Fruit,
    DarkGreenVeg,
    RedOrangeVeg,
    LegumesVeg,
    StarchyVeg,
    OtherVeg,
    AdditionalVeg,
    WholeGrain,
    EnrichedGrain,
    MeatAlt,
    Milk,
    QtyOfPortion,
  } = recipe;

  contributions.Fruit = parseFloat(Fruit) || 0;
  contributions.DarkGreenVeg = parseFloat(DarkGreenVeg) || 0;
  contributions.RedOrangeVeg = parseFloat(RedOrangeVeg) || 0;
  contributions.LegumesVeg = parseFloat(LegumesVeg) || 0;
  contributions.StarchyVeg = parseFloat(StarchyVeg) || 0;
  contributions.OtherVeg = parseFloat(OtherVeg) || 0;
  contributions.AdditionalVeg = parseFloat(AdditionalVeg) || 0;
  contributions.WholeGrain = parseFloat(WholeGrain) || 0;
  contributions.EnrichedGrain = parseFloat(EnrichedGrain) || 0;
  contributions.MeatAlt = parseFloat(MeatAlt) || 0;
  contributions.Milk = parseFloat(Milk) || 0;

  const qtyOfPortion = parseFloat(QtyOfPortion) || 0;

  // Computes the contributions per measure
  Object.entries(contributions).forEach(([key, value]) => {
    if (qtyOfPortion > 0) {
      contributions[key] = value /= qtyOfPortion;
    }
  });

  contributions.TotalVeg = calculateTotalVeg(contributions);
  contributions.TotalGrain = calculateTotalGrain(contributions);

  return contributions;
};

/**
 * Converts a recipe object to a cycle menu detail object.
 * @param {String} menuId the cycle menu id
 * @param {Object} recipe the recipe object
 * @param {Number} dayNumber the day number on the cycle
 * @param {Number} servings the total projected servings
 */
export const convertRecipeToMenuItem = ({
  menuId,
  recipe,
  dayNumber,
  servings,
}) => {
  const contributionsPerMeasure = calculateContributionsPerMeasure(recipe);

  return {
    [CycleMenuRecipeFields.CycleMenuId]: menuId,
    [CycleMenuRecipeFields.RecipeId]: recipe.Id,
    [CycleMenuRecipeFields.RecipeCategoryId]: recipe.RecipeCategoryId,
    [CycleMenuRecipeFields.DayNumber]: parseInt(dayNumber, 10),
    [CycleMenuRecipeFields.QtyOfPortion]: parseFloat(recipe.QtyOfPortion) || 0,
    [CycleMenuRecipeFields.ProjectedServings]: servings,
    [CycleMenuRecipeFields.DisplayOnCalendar]: true,
    [CycleMenuRecipeFields.Name]: recipe.Description,
    [CycleMenuRecipeFields.IsParent]: true,
    [CycleMenuRecipeFields.Children]: [],
    [CycleMenuRecipeFields.RecipeMeasure]: recipe.MeasureOfPortion || '',
    [CycleMenuRecipeFields.ContributionsPerMeasure]: contributionsPerMeasure,
    [CycleMenuRecipeFields.ServingsPercentage]: String(
      calculateServingsPercentage({
        projectedServings: servings,
        servings,
      })
    ),
    [CycleMenuRecipeFields.ComponentType]:
      parseInt(recipe.ComponentType, 10) || 0,
    [CycleMenuRecipeFields.Alacarte]: false,
  };
};

export const convertDetailToMenuItem = (cycleMenuDetail, servings) => {
  return {
    CycleMenuId: cycleMenuDetail.CycleMenuID,
    RecipeId: cycleMenuDetail.RecipeId,
    DayNumber: parseInt(cycleMenuDetail.DayNumber, 10),
    QtyOfPortion: cycleMenuDetail.QtyOfPortion,
    DisplayOnCalendar: cycleMenuDetail.DisplayOnCalendar === 'Y',
    Name: cycleMenuDetail.RecipeDescription,
    IsParent: cycleMenuDetail.IsParent,
    Children: cycleMenuDetail.Children || [],
    ProjectedServings: cycleMenuDetail.ProjectedServings,
    RecipeCategoryId: cycleMenuDetail.RecipeCategoryId,
    OrderNum: cycleMenuDetail.OrderNum,
    RecipeMeasure: cycleMenuDetail.RecipeMeasure,
    RecipeInactive: cycleMenuDetail.RecipeInactive,
    ContributionsPerMeasure: cycleMenuDetail.ContributionsPerMeasure,
    Alacarte: cycleMenuDetail.Alacarte === 'Y',
    ServingsPercentage: String(
      calculateServingsPercentage({
        projectedServings: cycleMenuDetail.ProjectedServings,
        servings,
      })
    ),
    ComponentType: parseInt(cycleMenuDetail.RecipeComponentType, 10) || 0,
  };
};

/**
 * Adds the OrderNum property to each of the cycle menu details.
 *
 * @param {Array{Array}} cycleMenuDetails the list of cycle menu details containing their list of items
 */
export const addOrderNumToDetails = (cycleMenuDetails) => {
  return cycleMenuDetails.map((detail) =>
    detail.map((item, i) => {
      return { ...item, OrderNum: i };
    })
  );
};

/**
 * Determines if the cycle menu detail contains the recipe and an a la carte recipe.
 *
 * @param {Object} recipesById the recipe draggable ids mapped to their recipe
 * @param {String} recipe.RecipeId the recipe id
 * @param {Array{String}} recipeDraggableIds the list of recipe draggable ids on the day
 * @returns {Object} boolean properties indicating if the type of recipe was found, both are false if not found
 */
export const findRecipe = (recipesById, recipe, recipeDraggableIds = []) => {
  const recipes = recipeDraggableIds.map((id) => recipesById[id]);

  let reimbursable = false;
  let alacarte = false;

  for (const item of recipes) {
    if (item.RecipeId === recipe.RecipeId && item.Alacarte) {
      alacarte = true;
    } else if (item.RecipeId === recipe.RecipeId) {
      reimbursable = true;
    }
  }

  return { reimbursable, alacarte };
};

/**
 * Creates the initial object used for the menu days map.
 *
 * @param {Number} numberOfDays total days in the cycle
 * @return {Object} the cycle menu days map
 */
export const createDaysMap = (numberOfDays) => {
  const menuDaysMap = {};

  // Create the day mapping
  for (let i = 0; i < numberOfDays; i++) {
    menuDaysMap[i + 1] = [];
  }

  return menuDaysMap;
};

export const orderMenuDaysMap = (
  menuDaysMap,
  recipesById,
  categories,
  options = { alacarteCategory: false } // categories must include alacarte category when alacarte option flag is true
) => {
  const copy = {};

  Object.entries(menuDaysMap).forEach(([day, ids = []]) => {
    if (!copy[day]) {
      copy[day] = [];
    }
    const categoriesMap = {};
    ids.forEach((id, index) => {
      const recipe = recipesById[id];
      if (recipe) {
        if (!categoriesMap[recipe.RecipeCategoryId]) {
          categoriesMap[recipe.RecipeCategoryId] = [];
        }

        if (options.alacarteCategory) {
          if (recipe.Alacarte) {
            if (!categoriesMap[ALACARTE_RECIPE_CATEGORY.Id]) {
              categoriesMap[ALACARTE_RECIPE_CATEGORY.Id] = [];
            }
            categoriesMap[ALACARTE_RECIPE_CATEGORY.Id].push(id);
            return;
          }
        }

        categoriesMap[recipe.RecipeCategoryId].push(id);
      }
    });

    categories.forEach((c) => {
      (categoriesMap[c.Id] || []).forEach((id) => {
        copy[day].push(id);
      });
    });
  });

  return copy;
};

export const updateMapAndId = (
  menuDaysMap,
  recipesById,
  categories,
  options = { alacarteCategory: false }
) => {
  const mapCopy = orderMenuDaysMap(
    menuDaysMap,
    recipesById,
    categories,
    options
  );
  const recipesByIdCopy = {
    ...recipesById,
  };

  Object.entries(mapCopy).forEach(([day, ids = []]) => {
    const categoryMap = {};
    let alacarte = false;
    let index = 0;
    ids.forEach((id, cIndex) => {
      const recipe = recipesByIdCopy[id];
      const DayNumber = parseInt(day, 10);
      if (!categoryMap[recipe.RecipeCategoryId]) {
        index += 1;
        categoryMap[recipe.RecipeCategoryId] = true;
      }
      if (options.alacarteCategory && !alacarte && recipe.Alacarte) {
        alacarte = true;
        index += 1;
      }
      recipe.OrderNum = index;
      index += 1;
      recipe.DayNumber = DayNumber;
      // update child's day number to match parents
      recipe.Children.forEach((childId, childIndex) => {
        const childCopy = {
          ...recipesByIdCopy[childId],
          DayNumber,
          OrderNum: childIndex,
        };
        recipesByIdCopy[childId] = {
          ...childCopy,
        };
      });
    });
  });
  return {
    recipesById: recipesByIdCopy,
    menuDaysMap: mapCopy,
  };
};

export const flattenMenuDaysMap = (menuDaysMap) => {
  return _.flattenDeep(
    Object.values(menuDaysMap).map((ids) => ids.map((id) => id))
  );
};

export const mapIdsToRecipe = (
  ids = [],
  recipeByIds = {},
  level = 0,
  setOrderNum,
  keepShortKeyId = false
) => {
  return ids.map((id, index) => {
    const recipe = {
      ...recipeByIds[id],
    };

    if (keepShortKeyId) {
      recipe[CycleMenuRecipeFields.ShortId] = id;
    }

    if (setOrderNum) {
      recipe.OrderNum = index;
    }

    recipe.Children = Array.isArray(recipe.Children)
      ? mapIdsToRecipe(recipe.Children, recipeByIds, level + 1, setOrderNum)
      : [];

    recipe.IsParent = recipe.Children.length > 0;

    return recipe;
  });
};

export const flattenMapToRecipe = (
  menuDaysMap,
  recipeByIds,
  keepShortKeyId = false
) => {
  const ids = flattenMenuDaysMap(menuDaysMap);
  return mapIdsToRecipe(ids, recipeByIds, 0, true, keepShortKeyId);
};

export const flattenMapSorted = (menuDaysMap, recipeByIds) => {
  return _.flattenDeep(
    Object.values(menuDaysMap).map((ids) => {
      return mapIdsToRecipe(ids, recipeByIds, 0, true);
    })
  );
};

export const groupItemsByCategory = (
  items = [],
  recipesById,
  groupByAlacarte = false
) => {
  const itemsWithDraggableId = items.map((id) => {
    const recipe = {
      ...recipesById[id],
      RecipeCategoryId: _.isNil(recipesById[id].RecipeCategoryId)
        ? null
        : recipesById[id].RecipeCategoryId,
      draggableId: id,
      level: 0,
    };
    const children = recipe.Children || [];

    const childrenRecipes = children.map((id) => {
      const childRecipe = recipesById[id];

      return {
        ...childRecipe,
        RecipeCategoryId: _.isNil(childRecipe.RecipeCategoryId)
          ? null
          : childRecipe.RecipeCategoryId,
        draggableId: id,
        level: 1,
      };
    });

    recipe.Children = childrenRecipes;

    return [recipe, childrenRecipes];
  });

  if (!groupByAlacarte) {
    return _.groupBy(_.flattenDeep([itemsWithDraggableId]), 'RecipeCategoryId');
  }

  const alacarteItems = [];
  const otherItems = [];

  itemsWithDraggableId.forEach(([parent, child]) => {
    if (parent.Alacarte) {
      alacarteItems.push(parent);
    } else {
      otherItems.push(parent);
      otherItems.push(child);
    }
  });
  const itemsGroupedByCategory = _.groupBy(
    _.flattenDeep(otherItems),
    'RecipeCategoryId'
  );
  itemsGroupedByCategory[ALACARTE_RECIPE_CATEGORY.Id] = alacarteItems;
  return itemsGroupedByCategory;
};

export const formatCycleMenuHeaderText = ({
  menuTitle,
  fiscalYear,
  servings,
  numberOfDays,
  nutritionStandard,
}) => {
  let text = '';

  text = menuTitle ? `${menuTitle}` : text;
  text = fiscalYear ? `${text} - ${fiscalYear}` : text;
  text = servings ? `${text} - ${servings} Servings` : text;
  text = numberOfDays ? `${text} - ${numberOfDays} Days` : text;
  text = nutritionStandard ? `${text} - ${nutritionStandard}` : text;
  return text;
};

export const applyPortionRatio = (cycleMenuDetail) => {
  const { ContributionsPerMeasure, QtyOfPortion } = cycleMenuDetail;
  const updatedContributions = { ...ContributionsPerMeasure };

  Object.keys(updatedContributions).forEach((key) => {
    const contributionAmount = updatedContributions[key] || 0;
    updatedContributions[key] = Number(
      Number(contributionAmount * QtyOfPortion).toFixed(3)
    );
  });

  const {
    DarkGreenVeg,
    RedOrangeVeg,
    LegumesVeg,
    StarchyVeg,
    OtherVeg,
    AdditionalVeg,
    WholeGrain,
    EnrichedGrain,
    Grain,
    Fruit,
  } = updatedContributions;

  updatedContributions.TotalVeg =
    DarkGreenVeg +
    RedOrangeVeg +
    LegumesVeg +
    StarchyVeg +
    OtherVeg +
    AdditionalVeg;

  updatedContributions.TotalVeg = parseFloat(!hasValue(updatedContributions.TotalVeg) ? 0 : updatedContributions.TotalVeg.toFixed(3));

  updatedContributions.TotalGrain =
    Grain && !isNaN(Grain) ? Grain : WholeGrain + EnrichedGrain;

  //note that we want to pass fruit and veg juice to the server in cups, not %
  //the contributions can come from many places, so just let the server calc it
  if (cycleMenuDetail.ComponentType === COMPONENT_TYPE.Juice) {
    updatedContributions[Fields.FruitJuice] = Fruit;
    updatedContributions[Fields.VegJuice] = updatedContributions.TotalVeg;
  } else {
    updatedContributions[Fields.FruitJuice] = 0;
    updatedContributions[Fields.VegJuice] = 0;
  }

  if (cycleMenuDetail.ComponentType === COMPONENT_TYPE.Dessert) {
    updatedContributions[Fields.GrainDessert] = updatedContributions.TotalGrain;
  } else {
    updatedContributions[Fields.GrainDessert] = 0;
  }

  return updatedContributions;
};

export const formatContribution = (contribution) =>
  Number(contribution.toFixed(3));

export const getMealContributionMessage = (cycleMenuDetail) => {
  const contributions = applyPortionRatio(cycleMenuDetail);
  const {
    Fruit,
    DarkGreenVeg,
    RedOrangeVeg,
    LegumesVeg,
    StarchyVeg,
    OtherVeg,
    AdditionalVeg,
    TotalGrain,
    MeatAlt,
    Milk,
  } = contributions;
  const messages = [];

  if (Fruit > 0) {
    messages.push(`${formatContribution(Fruit)} Fruit`);
  }

  if (DarkGreenVeg > 0) {
    messages.push(`${formatContribution(DarkGreenVeg)} Dark Green Veg.`);
  }

  if (RedOrangeVeg > 0) {
    messages.push(`${formatContribution(RedOrangeVeg)} Red/Orange Veg.`);
  }

  if (LegumesVeg > 0) {
    messages.push(`${formatContribution(LegumesVeg)} Beans, Peas, Lentils Veg.`);
  }

  if (StarchyVeg > 0) {
    messages.push(`${formatContribution(StarchyVeg)} Starchy Veg.`);
  }

  if (OtherVeg > 0) {
    messages.push(`${formatContribution(OtherVeg)} Other Veg.`);
  }

  if (AdditionalVeg > 0) {
    messages.push(`${formatContribution(AdditionalVeg)} Additional Veg.`);
  }

  if (MeatAlt > 0) {
    messages.push(`${formatContribution(MeatAlt)} Meat`);
  }

  if (TotalGrain > 0) {
    messages.push(`${formatContribution(TotalGrain)} Grain`);
  }

  if (Milk > 0) {
    messages.push(`${formatContribution(Milk)} Milk`);
  }

  return messages.join(', ');
};

/**
 * Determines the week or number of weeks associated with the given day number and analysis period.
 *
 * @param {Number} dayNumber
 * @param {Number} analysisDays
 * @returns {Number} the total weeks or the week number the day falls on
 */
export const getNumberOfWeeks = (dayNumber, analysisDays) => {
  let week = Math.floor(dayNumber / analysisDays);

  if (dayNumber % analysisDays > 0) {
    week += 1;
  }

  return week;
};

export const getDaysToShow = (
  numberOfDays,
  startDate,
  analysisDays,
  schoolCalendar
) => {
  let dayNumbersToShow = [];

  for (let i = 0; i < numberOfDays; i++) {
    if (shouldShowDay(i + 1, startDate, analysisDays, schoolCalendar)) {
      dayNumbersToShow.push(i + 1);
    }
  }
  return dayNumbersToShow;
};

export const formatAnalysisValue = (value) => {
  const amount = Number(value);

  if (isNaN(amount)) {
    return '0';
  }

  if (amount !== 0) {
    return amount.toFixed(ANALYSIS_VALUE_PRECISION);
  }

  return amount;
};

export const formatRequirementText = (requirement) => {
  const { WeeklyAmountMin, WeeklyAmountMax, IsPercentageBased } = requirement;
  const words = [];

  if (!_.isNil(WeeklyAmountMin)) {
    const minAmount = IsPercentageBased
      ? `${WeeklyAmountMin}%`
      : WeeklyAmountMin;
    words.push(`min. ${minAmount}`);
  }

  if (!_.isNil(WeeklyAmountMax)) {
    const maxAmount = IsPercentageBased
      ? `${WeeklyAmountMax}%`
      : WeeklyAmountMax;
    words.push(`max. ${maxAmount}`);
  }

  if (words.length > 0) {
    return `(${words.join(', ')})`;
  }
  return '';
};

export const formatAnalysisDescription = (requirement) => {
  const { WeeklyAmount, IsPercentageBased } = requirement;

  const amount = formatAnalysisValue(WeeklyAmount);
  const amountText = IsPercentageBased ? `${amount}%` : amount;

  return `${amountText} ${formatRequirementText(requirement)}`;
};

/** Checks whether the child can be attached to a parent */
export const isValidAttachment = (parentRecipe, childRecipe, recipesById) => {
  if (!childRecipe || !parentRecipe || !recipesById) {
    return false;
  }

  if (childRecipe.RecipeCategoryId === RecipeCategoryEnum.Entree) {
    return false;
  }

  if (childRecipe.RecipeId === parentRecipe.RecipeId) {
    return false;
  }

  /** Checks if parent already has the recipe being attached */
  const hasRecipeAsChild = parentRecipe.Children.some((childId) => {
    return _.get(recipesById, [childId, 'RecipeId']) === childRecipe.RecipeId;
  });

  if (hasRecipeAsChild) {
    return false;
  }

  return parentRecipe.RecipeCategoryId === RecipeCategoryEnum.Entree;
};

export const shouldAllowCombine = (recipe) => {
  return RecipeCategoryEnum.Entree !== _.get(recipe, ['RecipeCategoryId']);
};

/**
 *
 * @param {Number} servings
 * @param {Number} projectedServings
 * @returns {Number}
 */
export const calculateServingsPercentage = ({
  servings,
  projectedServings,
}) => {
  let percentage = 0;
  if (servings > 0) {
    percentage = _.multiply(_.divide(projectedServings, servings), 100);
  }

  percentage = Number(percentage.toFixed(3));

  return percentage;
};

export const decrementRecipeCount = (
  recipeCountByDayMap,
  dayNumber,
  recipeId
) => {
  const currentDayCounts = _.get(recipeCountByDayMap, dayNumber, {});
  const currentCount = _.get(recipeCountByDayMap, [dayNumber, recipeId], 0);

  if (currentCount > 0) {
    _.set(recipeCountByDayMap, dayNumber, { ...currentDayCounts });
    _.set(recipeCountByDayMap, [dayNumber, recipeId], currentCount - 1);
  }
};

export const incrementRecipeCount = (
  recipeCountByDayMap,
  dayNumber,
  recipeId
) => {
  const currentDayCounts = _.get(recipeCountByDayMap, dayNumber, {});
  const currentCount = _.get(recipeCountByDayMap, [dayNumber, recipeId], 0);

  _.set(recipeCountByDayMap, dayNumber, { ...currentDayCounts });
  _.set(recipeCountByDayMap, [dayNumber, recipeId], currentCount + 1);
};

/** Count number of categories in list of ids, this assumes that the ids are in order and sorted by categories */
export const countCategories = (recipes = [], stopAtIndex = 0) => {
  const categoryMap = {};
  for (let i = 0; i < stopAtIndex; i += 1) {
    const r = recipes[i];
    if (r) {
      categoryMap[r.RecipeCategoryId] = true;
    }
    if (stopAtIndex <= i) {
      break;
    }
  }

  return recipes.length === 1 ? 1 : Object.keys(categoryMap).length;
};

export const countRecipesForCategory = (recipes = [], categoryId) => {
  let num = 0;
  recipes.forEach((r) => {
    if (r.RecipeCategoryId === categoryId) {
      num += 1;
    }
  });
  return num;
};

export const maxOrderNumForCategory = (recipes = [], categoryId) => {
  let max = 0;
  recipes.forEach((r) => {
    if (r.RecipeCategoryId === categoryId && r.OrderNum > max) {
      max = r.OrderNum;
    }
  });
  return max;
};

export const removeAlacarte = (recipes = []) => {
  return recipes.filter((r) => !r.Alacarte);
};

/**
 * Converts a recipe to a reimbursable meal.
 *
 * @param {Object} recipe the formatted recipe object
 * @param {Number} dayNumber
 * @param {Array[Object]} recipes recipes already on the menu
 */
export const convertRecipeToReimbMeal = (recipe, dayNumber, recipes) => {
  let DarkGreenVeg = 0;
  let RedOrangeVeg = 0;
  let LegumesVeg = 0;
  let StarchyVeg = 0;
  let OtherVeg = 0;
  let AdditionalVeg = 0;
  let WholeGrain = 0;
  let EnrichedGrain = 0;
  let Fruit = 0;
  let Milk = 0;
  let MeatAlt = 0;
  let FruitJuice = 0;
  let VegJuice = 0;
  let GrainDessert = 0;

  const Children = {};

  [recipe, ...recipes].forEach((r, i) => {
    if (i > 0) {
      Children[r.RecipeId] = r.RecipeId;
    }
    const childContributionsPerMeasure = applyPortionRatio(r);
    DarkGreenVeg += childContributionsPerMeasure[Fields.DarkGreenVeg];
    RedOrangeVeg += childContributionsPerMeasure[Fields.RedOrangeVeg];
    LegumesVeg += childContributionsPerMeasure[Fields.LegumesVeg];
    StarchyVeg += childContributionsPerMeasure[Fields.StarchyVeg];
    OtherVeg += childContributionsPerMeasure[Fields.OtherVeg];
    AdditionalVeg += childContributionsPerMeasure[Fields.AdditionalVeg];
    WholeGrain += childContributionsPerMeasure[Fields.WholeGrain];
    EnrichedGrain += childContributionsPerMeasure[Fields.EnrichedGrain];
    Fruit += childContributionsPerMeasure[Fields.Fruit];
    Milk += childContributionsPerMeasure[Fields.Milk];
    MeatAlt += childContributionsPerMeasure[Fields.MeatAlt];
    FruitJuice += childContributionsPerMeasure[Fields.FruitJuice];
    VegJuice += childContributionsPerMeasure[Fields.VegJuice];
    GrainDessert += childContributionsPerMeasure[Fields.GrainDessert];
  });

  const TotalVeg =
    parseFloat(!hasValue(DarkGreenVeg +
    RedOrangeVeg +
    LegumesVeg +
    StarchyVeg +
    OtherVeg +
    AdditionalVeg) ? 0 :
    (DarkGreenVeg +
      RedOrangeVeg +
      LegumesVeg +
      StarchyVeg +
      OtherVeg +
      AdditionalVeg).toFixed(3));

  const TotalGrain = WholeGrain + EnrichedGrain;

  //note that we want to pass fruit and veg juice to the server in cups, not %
  //the contributions can come from many places, so just let the server calc it

  return {
    RecipeId: recipe.RecipeId,
    Children,
    FruitJuice,
    VegJuice,
    GrainDessert,
    Fruit,
    DarkGreenVeg,
    RedOrangeVeg,
    LegumesVeg,
    StarchyVeg,
    OtherVeg,
    AdditionalVeg,
    WholeGrain,
    EnrichedGrain,
    MeatAlt,
    Milk,
    TotalVeg,
    TotalGrain,
    DayNumber: dayNumber,
    Inactive: false,
    Description: `${recipe.Name} Meal`,
    Name: `${recipe.Name}`,
  };
};

export const computeMealContributionValues = (meal, recipeMap) => {
  const entreeContributions = applyPortionRatio(recipeMap[meal.RecipeId]);
  const childContributions = Object.values(meal.Children).map(
    (childRecipeId) => {
      const childRecipe =
        _.get(recipeMap, `${meal.RecipeId}.Children.${childRecipeId}`) ||
        _.get(recipeMap, childRecipeId) ||
        EMPTY_RECIPE_WITH_EMPTY_CONTRIBUTIONS;
      return applyPortionRatio(childRecipe);
    }
  );
  const recipeContributions = [entreeContributions].concat(childContributions);

  const mealContributionValues = {};

  recipeContributions.forEach((recipeContribution) => {
    Object.keys(Fields).forEach((field) => {
      mealContributionValues[field] = _.add(
        parseFloat(mealContributionValues[field]) || 0,
        parseFloat(recipeContribution[field]) || 0
      );
    });
  });

  return mealContributionValues;
};

/**
 * Extracts the contributions from the meal and converts all values to numbers.
 * @param {Object} meal
 */
export const getMealContributionValues = (meal) => {
  const contributions = _.pick(meal, Object.values(Fields));

  Object.keys(contributions).forEach((k) => {
    contributions[k] = parseFloat(contributions[k]) || 0;
  });

  return contributions;
};

export const areContributionsModifiedByUser = (meal, recipeMap) => {
  const currentContributionValues = getMealContributionValues(meal);
  const expectedContributionValues = computeMealContributionValues(
    meal,
    recipeMap
  );

  return !_.isEqual(currentContributionValues, expectedContributionValues);
};

export const removeRecipeFromMeals = ({ meals, recipe, recipeMap }) => {
  const updatedMeals = { ...meals };

  Object.keys(updatedMeals).forEach((recipeId) => {
    let meal = updatedMeals[recipeId];
    const mealRecipe = recipeMap[recipeId];

    const isPartOfEntree = mealRecipe.Children[recipe.RecipeId];

    if (!isPartOfEntree) {
      const children = { ...meal.Children };
      delete children[recipe.RecipeId];
      let updatedMeal = { ...meal, Children: children };
      const canUpdate = !areContributionsModifiedByUser(meal, recipeMap);

      // Only update if the contributions have not been changed by the user
      if (canUpdate) {
        const newContributionValues = computeMealContributionValues(
          updatedMeal,
          recipeMap
        );
        updatedMeal = { ...updatedMeal, ...newContributionValues };
      } else {
        delete updatedMeals[recipeId].Children[recipe.RecipeId];
      }
      updatedMeals[recipeId] = updatedMeal;
    }
  });

  return updatedMeals;
};

export const addRecipeToMeals = ({ meals, recipe, recipeMap }) => {
  const updatedMeals = { ...meals };

  Object.keys(updatedMeals).forEach((recipeId) => {
    let meal = updatedMeals[recipeId];
    const canUpdate = !areContributionsModifiedByUser(meal, recipeMap);

    if (canUpdate) {
      let updatedMeal = { ...updatedMeals[recipeId] };
      updatedMeal.Children = {
        ...updatedMeal.Children,
        [recipe.RecipeId]: recipe.RecipeId,
      };
      const newContributionValues = computeMealContributionValues(
        updatedMeal,
        recipeMap
      );
      updatedMeal = { ...updatedMeal, ...newContributionValues };
      updatedMeals[recipeId] = updatedMeal;
    } else {
      const Children = updatedMeals[recipeId].Children;
      updatedMeals[recipeId] = {
        ...updatedMeals[recipeId],
        Children: { ...Children, [recipe.RecipeId]: recipe.RecipeId },
      };
    }
  });

  return updatedMeals;
};

export const formatApiReimbMeal = (reimbMeal) => {
  const Children = {};
  reimbMeal.Children.forEach((meal) => {
    Children[meal] = meal;
  });

  return { ...reimbMeal, Children };
};

export const getMealsOnDay = (reimbMealsByDayMap, day) => {
  const mealsMap = reimbMealsByDayMap[day];

  return mealsMap ? Object.values(mealsMap) : [];
};

export const formatMenuItemForRecipeMap = (recipe, recipesById) => {
  const updatedRecipe = { ...recipe };
  const recipeChildren = {};

  if (Array.isArray(recipe.Children)) {
    recipe.Children.forEach((recipeDraggableId) => {
      const childRecipe = recipesById[recipeDraggableId];
      const childRecipeId = childRecipe.RecipeId;
      recipeChildren[childRecipeId] = formatMenuItemForRecipeMap(
        childRecipe,
        recipesById
      );
    });
    updatedRecipe.Children = recipeChildren;
  }

  return updatedRecipe;
};

export const updateMealContributionTotals = (
  meal,
  field,
  hasVegTotalBeenEdited
) => {
  const mealCopy = { ...meal };

  const wholeGrain = parseFloat(meal[Fields.WholeGrain]) || 0;
  const enrichedGrain = parseFloat(meal[Fields.EnrichedGrain]) || 0;

  if (
    field !== Fields.TotalVeg &&
    VEGETABLE_CONTRIBUTION_FIELDS.includes(field) &&
    !hasVegTotalBeenEdited
  ) {
    const darkGreenVeg = parseFloat(meal[Fields.DarkGreenVeg]) || 0;
    const redOrangeVeg = parseFloat(meal[Fields.RedOrangeVeg]) || 0;
    const legumesVeg = parseFloat(meal[Fields.LegumesVeg]) || 0;
    const starchyVeg = parseFloat(meal[Fields.StarchyVeg]) || 0;
    const otherVeg = parseFloat(meal[Fields.OtherVeg]) || 0;
    const additionalVeg = parseFloat(meal[Fields.AdditionalVeg]) || 0;

    mealCopy.TotalVeg =
      darkGreenVeg +
      redOrangeVeg +
      legumesVeg +
      starchyVeg +
      otherVeg +
      additionalVeg;
    mealCopy.TotalVeg = parseFloat(!hasValue(mealCopy.TotalVeg) ? 0 : mealCopy.TotalVeg.toFixed(3));
  }

  mealCopy.TotalGrain = wholeGrain + enrichedGrain;

  return mealCopy;
};

export const formatDayText = ({
  weekNumber,
  showDaysOfTheWeek,
  analysisDays,
  dayNumber,
  startDate,
}) => {
  let text = '';

  if (weekNumber > 0) {
    text = `Week ${weekNumber}`;
  }

  text = `${text} Day ${dayNumber}`;

  let dayIndex = dayNumber % analysisDays;

  // Assumes day numbers start from 1
  if (dayIndex === 0) {
    dayIndex = analysisDays - 1;
  } else {
    dayIndex = dayIndex - 1;
  }

  if (showDaysOfTheWeek) {
    if (!startDate) {
      text = `${text} - ${DAYS_OF_THE_WEEK[dayIndex]}`;
    } else {
      const dayOfWeek = moment(startDate)
        .add(dayNumber - 1, 'days')
        .isoWeekday();
      text = `${text} - ${DAYS_OF_THE_WEEK[dayOfWeek - 1]}`;
    }
  }

  return text;
};

export const formatMonthHeaderText = ({
  showDaysOfTheWeek,
  analysisDays,
  dayNumber,
  startDate,
}) => {
  let text = '';

  if (showDaysOfTheWeek) {
    if (!startDate) {
      text = `${dayNumber}-${DAYS_OF_THE_WEEK[(dayNumber - 1) % analysisDays]}`;
    } else {
      const dayOfWeek = moment(startDate)
        .add(dayNumber - 1, 'days')
        .isoWeekday();
      text = `${dayNumber}-${DAYS_OF_THE_WEEK[dayOfWeek - 1]}`;
    }
  } else {
    text = dayNumber;
  }

  return text;
};

export const getDayOfWeek = ({ analysisDays, dayNumber, startDate }) => {
  if (!startDate) {
    return `${DAYS_OF_THE_WEEK[(dayNumber - 1) % analysisDays]}`;
  }
  const startDayOfWeek = moment(startDate)
    .add(dayNumber - 1, 'days')
    .isoWeekday();
  return `${DAYS_OF_THE_WEEK[startDayOfWeek - 1]}`;
};

export const isWeekendDay = (day) => {
  return day === DAYS_OF_THE_WEEK[5] || day === DAYS_OF_THE_WEEK[6];
};

const isThereACalendarExceptionForDay = (day, startDate, schoolCalendar) => {
  const date = moment(startDate).add(day, 'days');
  if (schoolCalendar) {
    return (
      schoolCalendar.SchoolCalendarExceptions.filter((exception) => {
        return (
          date.isBetween(
            moment(exception.BeginDay),
            moment(exception.EndDay),
            'day'
          ) ||
          date.isSame(moment(exception.BeginDay), 'day') ||
          date.isSame(exception.EndDay)
        );
      }).length > 0
    );
  }
  return false;
};

export const shouldShowDay = (
  dayNumber,
  startDate,
  analysisDays,
  schoolCalendar
) => {
  //check if day is weekend day, if there is a calendar exception.
  //Also check if startDate is defined as it is null if not in applied menus mode
  if (
    startDate &&
    isWeekendDay(getDayOfWeek({ dayNumber, startDate, analysisDays })) &&
    !isThereACalendarExceptionForDay(dayNumber - 1, startDate, schoolCalendar)
  ) {
    return false;
  }
  return true;
};

export const normalizeDetailForAPI = (detail) => ({
  ...detail,
  QtyOfPortion: parseFloat(detail.QtyOfPortion) || 0,
  ProjectedServings: parseFloat(detail.ProjectedServings) || 0,
});

export const formatDetailForAnalysis = (detail) => {
  const normalizedChildren = detail.Children.map((c) =>
    normalizeDetailForAPI(c)
  );
  const normalizedDetail = normalizeDetailForAPI(detail);
  normalizedDetail.Children = normalizedChildren;

  return normalizedDetail;
};

export const hasValidServingSize = (recipe) => {
  return Boolean(recipe[RecipeFields.QtyOfPortion]);
};

/**
 * Determines a recipe's DisplayOnCalendar based on if it is already
 * on the menu. Defaults to true if it does not exist on the menu.
 * NOTE: Alacarte DisplayOnCalendar is defaulted to false
 * and is handled in the add actions.
 */
export const getRecipeDisplayOnMenu = (recipesById, recipeId) => {
  const usedOnMenu = Object.keys(recipesById).some(
    (id) => recipesById[id][CycleMenuRecipeFields.RecipeId] === recipeId
  );
  const displayedOnMenu = Object.keys(recipesById).some(
    (id) =>
      recipesById[id][CycleMenuRecipeFields.RecipeId] === recipeId &&
      recipesById[id][CycleMenuRecipeFields.DisplayOnCalendar]
  );

  if (!usedOnMenu) {
    return true;
  }
  return displayedOnMenu;
};

export const updateRecipeDisplayOnMenu = ({
  recipesById,
  displayOnCalendar,
  recipeId,
}) => {
  const updatedRecipesById = _.cloneDeep(recipesById);
  const recipeIdsToHide = Object.keys(recipesById).filter(
    (draggableId) =>
      recipesById[draggableId][CycleMenuRecipeFields.RecipeId] === recipeId
  );

  recipeIdsToHide.forEach((draggableId) => {
    const updatedRecipe = {
      ...recipesById[draggableId],
      [CycleMenuRecipeFields.DisplayOnCalendar]: displayOnCalendar,
    };

    if (
      Array.isArray(updatedRecipe[CycleMenuRecipeFields.Children]) &&
      updatedRecipe[CycleMenuRecipeFields.Children].length > 0
    ) {
      updatedRecipe.Children.forEach((id) => {
        if (recipesById[id]) {
          const childCopy = {
            ...recipesById[id],
            DisplayOnCalendar: displayOnCalendar,
          };
          recipesById[id] = childCopy;
        }
      });
    }

    updatedRecipesById[draggableId] = updatedRecipe;
  });

  return updatedRecipesById;
};

export const canEditAppliedMenu = ({
  menuType,
  editableAppliedDays,
  dayNumber,
}) => {
  if (menuType === EDIT_MENU_TYPES.APPLIED_MENU) {
    return editableAppliedDays[dayNumber];
  }
  return true;
};

export const findRecipes = (searchTerm, recipeList) => {
  if (_.isNil(searchTerm) || searchTerm === '' || searchTerm.trim() === '') {
    return recipeList;
  }

  const query = searchTerm.trim().toLowerCase();

  return recipeList.filter((rec) => {
    const name = (rec[RecipeFields.Name] || '').toLowerCase();
    const recId = (rec[RecipeFields.Id] || '').toLowerCase();

    return name.includes(query) || recId === query;
  });
};

/**
 * Checks if a recipe can be replaced. An entree recipe cannot be replaced with a non-entree recipe if it has
 * items attached to it.
 * @param {Object} recipesById recipe shortids mapped to their recipe objects
 * @param {Array[Object]} recipeIds
 * @returns {List[Object]} the list of entree recipes
 */
export const getIrreplaceableRecipes = (
  recipesById,
  recipeIds,
  replacementRecipe
) => {
  const recipes = recipeIds.map((rId) => recipesById[rId]);

  return recipes.filter(
    (rec) =>
      ProdRecordHelpers.isEntree(rec[CycleMenuRecipeFields.RecipeCategoryId]) &&
      rec[CycleMenuRecipeFields.Children].length > 0 &&
      !ProdRecordHelpers.isEntree(
        replacementRecipe[CycleMenuRecipeFields.RecipeCategoryId]
      )
  );
};

export const getIrreplaceableRecipeMsg = (recipes) => {
  const messages = recipes.map(
    (rec) =>
      `${rec[CycleMenuRecipeFields.RecipeId]} ${
        rec[CycleMenuRecipeFields.Name]
      } - Day ${rec[CycleMenuRecipeFields.DayNumber]}`
  );

  return messages.join('\n');
};

/**
 * Returns the days that would have duplicate recipes as a result of replacing.
 *
 * @param {Array{String}} recipeIds - recipe short ids
 * @param {Object} recipesById - short ids mapped to recipe object
 * @param {Object} recipeCountByDayMap day mapped to map of recipe ids and their counts
 * @param {Object} replacementRecipe - recipe object
 */
export const getReplaceRecipeDuplicateDays = (
  recipeIds,
  recipesById,
  recipeCountByDayMap,
  replacementRecipe
) => {
  const copy = _.cloneDeep(recipeCountByDayMap);
  const days = [];

  recipeIds.forEach((rId) => {
    const recipe = recipesById[rId];
    const recipeDay = recipe[CycleMenuRecipeFields.DayNumber];
    const replacementRecipeId = replacementRecipe[RecipeFields.Id];
    const path = [recipeDay, replacementRecipeId];
    const currentCount = _.get(copy, path, 0);
    _.set(copy, path, currentCount + 1);
  });

  Object.keys(copy).forEach((day) => {
    const recipeCountMap = copy[day];

    const hasDuplicates = Object.keys(recipeCountMap).some(
      (rId) => recipeCountMap[rId] > 1
    );

    if (hasDuplicates) {
      days.push(day);
    }
  });

  return days;
};

export const addReimbMeal = (reimbMealsByDayMap, reimbMeal, dayNumber) => {
  const updatedMap = _.cloneDeep(reimbMealsByDayMap);

  _.set(updatedMap, [dayNumber, reimbMeal.RecipeId], reimbMeal);

  return updatedMap;
};



// WEBPACK FOOTER //
// ./src/pages/managemenu/subpages/editmenu/utilities/index.js